import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BookOpen, Calendar, CreditCard, X } from "lucide-react";
import SummaryApi from "../common";
import { toast } from "react-toastify";

const RenewalForm = ({ onRenew, currentUser, onClose }) => {
  const [plans, setPlans] = useState([]);
  const [planTypes, setPlanTypes] = useState([]);
  const [selectedPlanType, setSelectedPlanType] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [isRenewing, setIsRenewing] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  // Fetch plans from backend
  const fetchPlans = async () => {
    try {
      const res = await fetch(SummaryApi.getAllPlans.url);
      if (!res.ok) throw new Error("Failed to fetch plans");
      const data = await res.json();

      // Handle response format
      let resolvedPlans = [];
      if (data.success && Array.isArray(data.plans)) {
        resolvedPlans = data.plans;
      } else if (Array.isArray(data)) {
        resolvedPlans = data;
      } else if (Array.isArray(data.data)) {
        resolvedPlans = data.data;
      }

      setPlanTypes(resolvedPlans);

      // Auto-select first plan type and its first duration
      if (resolvedPlans.length > 0 && resolvedPlans[0].plans?.length > 0) {
        setSelectedPlanType(resolvedPlans[0]);
        setSelectedDuration(resolvedPlans[0].plans[0]);
      }
    } catch (err) {
      console.error("Error fetching plans:", err);
      alert("❌ Unable to load subscription plans. Please try again later.");
      setPlanTypes([]);
    }
  };

  useEffect(() => {
    fetchPlans();

    // Load Razorpay SDK
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    document.body.appendChild(script);
  }, []);

  // Calculate renewal dates based on current membership
  const calculateRenewalDates = (durationInMonths) => {
    if (!durationInMonths || isNaN(durationInMonths)) {
      return { newStartDate: null, newEndDate: null };
    }

    const durationDays = durationInMonths * 30;
    const today = new Date();

    const lastMembership =
      currentUser?.membershipsDetails?.[
        currentUser.membershipsDetails.length - 1
      ];

    let currentEndDate = null;
    if (lastMembership?.endDate) {
      currentEndDate = new Date(lastMembership.endDate);
    }

    // If current membership is still active, start from its end date
    const newStartDate =
      currentEndDate &&
      !isNaN(currentEndDate.getTime()) &&
      today < currentEndDate
        ? new Date(currentEndDate)
        : new Date(today);

    const newEndDate = new Date(newStartDate);
    newEndDate.setDate(newEndDate.getDate() + durationDays);

    return { newStartDate, newEndDate };
  };

  const handlePlanTypeSelect = (planType) => {
    setSelectedPlanType(planType);
    // Auto-select first duration of this plan type
    if (planType.plans && planType.plans.length > 0) {
      setSelectedDuration(planType.plans[0]);
    } else {
      setSelectedDuration(null);
    }
  };

  const handleDurationSelect = (duration) => {
    setSelectedDuration(duration);
  };

  const handleSubmit = async () => {
    if (!selectedPlanType || !selectedDuration) {
      alert("Please select a plan and duration to continue.");
      return;
    }

    setIsRenewing(true);

    const { newStartDate, newEndDate } = calculateRenewalDates(
      selectedDuration.durationInMonths
    );

    // Validate dates
    if (
      !newStartDate ||
      !newEndDate ||
      isNaN(newStartDate.getTime()) ||
      isNaN(newEndDate.getTime())
    ) {
      alert("Invalid date calculation. Please try again.");
      setIsRenewing(false);
      return;
    }

    try {
      // 1. Create Razorpay Order
      const orderRes = await fetch(SummaryApi.createOrder.url, {
        method: SummaryApi.createOrder.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: selectedDuration.price }),
      });

      const orderData = await orderRes.json();
      if (!orderData.success) throw new Error("Order creation failed");

      // 2. Close any open modals before Razorpay popup
      const renewalModal = document.getElementById("renewal-modal");
      const subscriptionModal = document.getElementById(
        "subscription-renewal-modal"
      );
      if (renewalModal?.open) renewalModal.close();
      if (subscriptionModal?.open) subscriptionModal.close();

      await new Promise((resolve) => setTimeout(resolve, 120));
      console.log(process.env.REACT_APP_RAZORPAY_KEY_ID);
      // 3. Configure Razorpay
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.order.amount,
        currency: "INR",
        name: "Library Subscription Renewal",
        description: `${selectedPlanType.planType} Plan - ${selectedDuration.durationInMonths} Month(s)`,
        order_id: orderData.order.id,
        handler: async function (response) {
          try {
            const verifyPayload = {
              userId: currentUser._id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planType: selectedPlanType.planType,
              durationInMonths: selectedDuration.durationInMonths,
              cost: selectedDuration.price,

              startDate: newStartDate.toISOString(),
              endDate: newEndDate.toISOString(),
            };

            console.log("📦 Sending renewal payload:", verifyPayload);

            const verifyRes = await fetch(SummaryApi.renewSubscription.url, {
              method: SummaryApi.renewSubscription.method,
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify(verifyPayload),
            });
            const verifyData = await verifyRes.json();
            console.log(verifyData);
            if (verifyData.success && verifyData.token) {
              localStorage.setItem("authToken", verifyData.token); //new token so route file detect it fix stale state

              toast.success("Renewal successful!");

              // if(location.pathname == "/library-dashboard"){
              navigate("/library-dashboard");
              window.location.reload();
              // }else{

              // }
            } else {
              toast.error("Renewal failed: " + verifyData.message);
            }
          } catch (err) {
            console.error("Verification error:", err);
            alert("❌ Error verifying payment. Please contact support.");
          } finally {
            setIsRenewing(false);
          }
        },
        theme: { color: "#22c55e" },
      };

      // 5. Open Razorpay checkout
      const rzp = new window.Razorpay(options);
      rzp.open();

      // 6. Handle payment failure
      rzp.on("payment.failed", function () {
        alert("❌ Payment failed, please try again.");
        if (subscriptionModal) subscriptionModal.showModal();
        if (renewalModal) renewalModal.showModal();
        setIsRenewing(false);
      });
    } catch (err) {
      console.error("Renewal error:", err);
      alert("❌ Something went wrong. Please try again.");
      setIsRenewing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg max-h-[85vh] overflow-y-auto">
      <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
        <div className="flex items-center gap-3">
          <BookOpen size={32} className="text-green-500" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              Renew Your Membership
            </h1>
            <p className="text-sm text-gray-600">
              Select a plan to continue your library access
            </p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X size={24} />
          </button>
        )}
      </div>

      <div className="p-6">
        {/* Current Membership Info Card */}
        {currentUser?.membershipsDetails?.length > 0 && (
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-5 mb-6 border border-green-200">
            <div className="flex items-center gap-3 mb-3">
              <Calendar className="text-green-600" size={24} />
              <h2 className="text-lg font-semibold text-gray-800">
                Current Membership Status
              </h2>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Plan Type</p>
                <p className="font-semibold text-gray-800 text-base">
                  {currentUser.membershipsDetails[
                    currentUser.membershipsDetails.length - 1
                  ]?.plan || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Expires On</p>
                <p className="font-semibold text-gray-800 text-base">
                  {currentUser.membershipsDetails[
                    currentUser.membershipsDetails.length - 1
                  ]?.endDate
                    ? new Date(
                        currentUser.membershipsDetails[
                          currentUser.membershipsDetails.length - 1
                        ].endDate
                      ).toLocaleDateString("en-IN", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Plan Selection */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Select Plan Type
            </h3>

            {planTypes.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                Loading available plans...
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {planTypes.map((planType) => (
                  <div
                    key={planType._id}
                    onClick={() => handlePlanTypeSelect(planType)}
                    className={`relative bg-white rounded-xl shadow-md p-5 border-2 cursor-pointer transition-all hover:shadow-lg ${
                      selectedPlanType?._id === planType._id
                        ? "border-green-500 ring-2 ring-green-200 bg-green-50"
                        : "border-gray-200 hover:border-green-300"
                    }`}
                  >
                    {selectedPlanType?._id === planType._id && (
                      <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                        ✓
                      </div>
                    )}

                    <h3 className="text-xl font-bold text-gray-800 mb-3 text-center">
                      {planType.planType}
                    </h3>

                    {planType.features && planType.features.length > 0 && (
                      <ul className="space-y-1 text-sm text-gray-600">
                        {planType.features.slice(0, 3).map((feature, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-green-500 mt-0.5">✓</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                        {planType.features.length > 3 && (
                          <li className="text-xs text-gray-500 italic">
                            +{planType.features.length - 3} more features
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Duration Selection */}
          {selectedPlanType &&
            selectedPlanType.plans &&
            selectedPlanType.plans.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Select Duration
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedPlanType.plans.map((duration, idx) => {
                    const { newStartDate, newEndDate } = calculateRenewalDates(
                      duration.durationInMonths
                    );

                    return (
                      <div
                        key={idx}
                        onClick={() => handleDurationSelect(duration)}
                        className={`relative bg-white rounded-xl shadow-md p-5 border-2 cursor-pointer transition-all hover:shadow-lg ${
                          selectedDuration?.durationInMonths ===
                            duration.durationInMonths &&
                          selectedDuration?.price === duration.price
                            ? "border-green-500 ring-2 ring-green-200 bg-green-50"
                            : "border-gray-200 hover:border-green-300"
                        }`}
                      >
                        {selectedDuration?.durationInMonths ===
                          duration.durationInMonths &&
                          selectedDuration?.price === duration.price && (
                            <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                              ✓
                            </div>
                          )}

                        <div className="text-center mb-3">
                          <div className="text-3xl font-bold text-green-600">
                            ₹{duration.price}
                          </div>
                          <div className="text-gray-600 text-sm mt-1">
                            for {duration.durationInMonths} month
                            {duration.durationInMonths > 1 ? "s" : ""}
                          </div>
                        </div>

                        {newStartDate && newEndDate && (
                          <div className="border-t pt-3 mt-3">
                            <div className="text-xs text-gray-500 space-y-1">
                              <p>
                                <span className="font-semibold">Starts:</span>{" "}
                                {newStartDate.toLocaleDateString("en-IN", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </p>
                              <p>
                                <span className="font-semibold">Ends:</span>{" "}
                                {newEndDate.toLocaleDateString("en-IN", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
        </div>

        {/* Payment Section */}
        <div className="mt-8 pt-6 border-t">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              {selectedPlanType && selectedDuration && (
                <div>
                  <p className="text-sm text-gray-600">Selected Plan</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {selectedPlanType.planType} -{" "}
                    {selectedDuration.durationInMonths} month
                    {selectedDuration.durationInMonths > 1 ? "s" : ""}
                  </p>
                  <p className="text-2xl font-bold text-green-600 mt-1">
                    ₹{selectedDuration.price}
                  </p>
                </div>
              )}
            </div>

            <button
              onClick={handleSubmit}
              disabled={isRenewing || !selectedPlanType || !selectedDuration}
              className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white font-semibold px-8 py-4 rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg shadow-lg"
            >
              <CreditCard size={24} />
              {isRenewing ? "Processing Payment..." : "Pay & Renew Membership"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewalForm;
